import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Group, Button, Paper, TextInput, Stack, Title } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useAccountListData } from '../hooks/useAccountData.ts';
import DriverListTable from '../components/DriverListTable.tsx';
import { SectionState } from '../components/SectionState.tsx';

const PAGE_SIZE = 10;

const AccountListFilterBar = ({
  email: initialEmail,
  phoneNumber: initialPhoneNumber,
  onReset,
  onSearch,
}: {
  email: string;
  phoneNumber: string;
  onReset: () => void;
  onSearch: (email: string, phoneNumber: string) => void;
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);

  const handleReset = () => {
    setEmail('');
    setPhoneNumber('');
    onReset();
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(email, phoneNumber);
  };

  return (
    <form onSubmit={handleSearch}>
      <Group>
        <TextInput
          w={300}
          placeholder="搜尋 Email（請輸入完整 email）"
          leftSection={<IconSearch size={16} />}
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
        />
        <TextInput
          w={300}
          placeholder="搜尋手機號碼（例：0912345678）"
          leftSection={<IconSearch size={16} />}
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.currentTarget.value)}
        />
        <Button variant="default" type="submit">
          搜尋
        </Button>
        <Button variant="default" type="button" onClick={handleReset}>
          重設
        </Button>
      </Group>
    </form>
  );
};

const DriverAccountListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  const phoneNumber = searchParams.get('phoneNumber') ?? '';
  const page = Number(searchParams.get('page') ?? 1);

  const { data, isFetching, error } = useAccountListData({
    email: email || undefined,
    phoneNumber: phoneNumber || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const handleSearch = (email: string, phoneNumber: string) => {
    const next = new URLSearchParams();
    if (email.trim()) next.set('email', email.trim());
    if (phoneNumber.trim()) next.set('phoneNumber', phoneNumber.trim());
    setSearchParams(next);
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams());
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      prev.set('page', String(page));
      return prev;
    });
  };

  return (
    <Paper radius="lg" p="xl" shadow="xl">
      <Stack gap="md">
        <Title order={2}>駕駛人資料</Title>

        <AccountListFilterBar
          key={`${email}|${phoneNumber}`}
          email={email}
          phoneNumber={phoneNumber}
          onSearch={handleSearch}
          onReset={handleReset}
        />

        <DriverListTable
          data={data?.data ?? []}
          page={page}
          pageSize={PAGE_SIZE}
          totalRecords={data?.extra.totalElement ?? 0}
          fetching={isFetching}
          onPageChange={handlePageChange}
          emptyState={
            error ? (
              <SectionState status="error" error={error} />
            ) : (
              <SectionState status="empty" message="沒有駕駛人資料" />
            )
          }
          customLoader={<SectionState status="loading" />}
        />
      </Stack>
    </Paper>
  );
};

export default DriverAccountListPage;
